// TEMP: floating switcher to preview the switch wizard's dialogs — delete this file (and its use in
// switchSalesChannelDialog.tsx) before merging.
import React, { FC } from 'react';
import { createPortal } from 'react-dom';
import { useDialogManager } from 'src/components/widgets/dialog';
import styled from 'styled-components';
import CompleteSwitchDialog, { SwitchTaskCreatedDialog } from './completeSwitchDialog';
import CustomPlanFinancialPreviewDialog, { CustomPlanFinancialPreview } from './customPlanFinancialPreviewDialog';
import { SWITCH_DIALOG_IDS } from './switchTypes';

export type ReviewPreviewCase = 'real' | 'multipleNotices' | 'customPlanNotices' | 'shopifyApproval' | 'noNotices';

export interface ReviewNoticeFlags {
    masterCurrencyCode?: string | null;
    secondaryCurrencyCode?: string | null;
    requiresShopifyApproval: boolean;
    requiresCustomPlanSubscription: boolean;
    requiresPaymentMethodSetup: boolean;
    hasCurrencyMismatch: boolean;
}

/** The flags the Review step shows for the chosen case; 'real' keeps the ones computed from the two stores. */
export const applyReviewPreviewCase = (previewCase: ReviewPreviewCase, real: ReviewNoticeFlags): ReviewNoticeFlags => {
    switch (previewCase) {
        case 'multipleNotices':
            return {
                masterCurrencyCode: 'EUR',
                secondaryCurrencyCode: 'USD',
                requiresShopifyApproval: false,
                requiresCustomPlanSubscription: true,
                requiresPaymentMethodSetup: true,
                hasCurrencyMismatch: true
            };
        case 'customPlanNotices':
            // Two notices: the custom-plan subscription driven by a currency mismatch (both stores non-API, non-Shopify), no payment-method setup.
            return {
                masterCurrencyCode: 'EUR',
                secondaryCurrencyCode: 'USD',
                requiresShopifyApproval: false,
                requiresCustomPlanSubscription: true,
                requiresPaymentMethodSetup: false,
                hasCurrencyMismatch: true
            };
        case 'shopifyApproval':
            return {
                ...real,
                requiresShopifyApproval: true,
                requiresCustomPlanSubscription: false,
                requiresPaymentMethodSetup: false,
                hasCurrencyMismatch: false
            };
        case 'noNotices':
            return {
                ...real,
                requiresShopifyApproval: false,
                requiresCustomPlanSubscription: false,
                requiresPaymentMethodSetup: false,
                hasCurrencyMismatch: false
            };
        default:
            return real;
    }
};

const Panel = styled.div`
    position: fixed;
    left: 16px;
    bottom: 16px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px;
    background: #ffffff;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    font-size: 12px;
`;

const PanelTitle = styled.strong`
    font-size: 11px;
    text-transform: uppercase;
    color: #6b6b6b;
`;

const PanelDivider = styled.hr`
    width: 100%;
    margin: 4px 0;
    border: none;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
`;

const CaseButton = styled.button<{ $active?: boolean }>`
    padding: 6px 10px;
    border: 1px solid ${(props) => (props.$active ? props.theme.app.primary : props.theme.app.borderColor)};
    border-radius: 6px;
    background: ${(props) => (props.$active ? props.theme.app.primary : '#ffffff')};
    color: ${(props) => (props.$active ? '#ffffff' : props.theme.app.textColor)};
    font-size: 12px;
    text-align: left;
    cursor: pointer;
`;

const CASES: { value: ReviewPreviewCase; label: string }[] = [
    { value: 'real', label: 'Real data' },
    { value: 'multipleNotices', label: 'Multiple notices' },
    { value: 'customPlanNotices', label: 'Custom plan (2 notices)' },
    { value: 'shopifyApproval', label: 'Shopify plan approval' },
    { value: 'noNotices', label: 'No notices' }
];

// A believable financial-preview payload for the step-2 simulations. warnings[0] and warnings[last] are dropped by the
// dialog (it renders warnings.slice(1, -1)), so they are padded with empty strings.
const makeMockPreview = (newCurrencyCode: string, isEstimate = true): CustomPlanFinancialPreview => ({
    isEstimate,
    currentPlan: { monthlyPrice: 169.91, currencyCode: 'EUR' },
    newPlan: { monthlyPrice: 242.34, currencyCode: newCurrencyCode },
    proration: { available: true, remainingDays: 12 },
    paymentOnPlanChange: {
        estimatedCreditApplied: 0,
        estimatedRefundValue: 2905.27,
        currencyCode: newCurrencyCode,
        nextRenewalAtUtc: '2027-09-25T00:00:00Z',
        nextRenewalAmount: 2908.03
    },
    warnings: ['']
});

const SIM_DIALOG_ID = 'switch-preview-sim';

interface StepSimulation {
    label: string;
    render: (close: () => void) => React.ReactElement;
}

// Every distinct look of steps 2 and 3, so each can be opened in isolation without driving the real flow.
const STEP_SIMULATIONS: StepSimulation[] = [
    {
        label: 'Step 2 — currency change',
        render: (close) => (
            <CustomPlanFinancialPreviewDialog preview={makeMockPreview('AUD')} paymentMethodSetupRequired={false} onContinue={close} onClose={close} />
        )
    },
    {
        label: 'Step 2 — payment method required',
        render: (close) => (
            <CustomPlanFinancialPreviewDialog preview={makeMockPreview('USD')} paymentMethodSetupRequired={true} onContinue={close} onClose={close} />
        )
    },
    {
        label: 'Step 2 — final amounts (no estimate)',
        render: (close) => (
            <CustomPlanFinancialPreviewDialog preview={makeMockPreview('AUD', false)} paymentMethodSetupRequired={false} onContinue={close} onClose={close} />
        )
    },
    {
        label: 'Step 3 — switch task created',
        render: (close) => (
            <SwitchTaskCreatedDialog
                taskID={12345}
                masterSalesChannelName='My Shopify store'
                secondarySalesChannelName='My API store'
                onGoToTask={close}
                onClose={close}
            />
        )
    },
    {
        label: 'Step 3 — complete switch',
        render: (close) => <CompleteSwitchDialog onSwitchProducts={close} />
    }
];

export const ReviewPreviewSwitcher: FC<{ value: ReviewPreviewCase; onChange: (value: ReviewPreviewCase) => void }> = ({ value, onChange }) => {
    const { showDialog, closeDialog } = useDialogManager();
    // Closing a simulated step ends the whole preview: it dismisses the simulated dialog and the step-1 review that hosts
    // this panel, so the X behaves like exiting the wizard rather than dropping back onto step 1.
    const closeProcess = () => {
        closeDialog(SIM_DIALOG_ID);
        closeDialog(SWITCH_DIALOG_IDS.switchSalesChannel);
    };
    const openSimulation = (simulation: StepSimulation) => showDialog(SIM_DIALOG_ID, simulation.render(closeProcess));

    // Portalled to <body> so the panel escapes the step-1 dialog's overlay stacking context; otherwise a simulation
    // dialog opened on top would cover the panel and swallow its clicks.
    return createPortal(
        <Panel>
            <PanelTitle>Step 1 — notices</PanelTitle>
            {CASES.map((c) => (
                <CaseButton key={c.value} type='button' $active={value === c.value} onClick={() => onChange(c.value)}>
                    {c.label}
                </CaseButton>
            ))}
            <PanelDivider />
            <PanelTitle>Open steps 2 & 3</PanelTitle>
            {STEP_SIMULATIONS.map((simulation) => (
                <CaseButton key={simulation.label} type='button' onClick={() => openSimulation(simulation)}>
                    {simulation.label}
                </CaseButton>
            ))}
        </Panel>,
        document.body
    );
};
